import React from 'react';
import { AuditActivity } from '../types';
import { ActivityBadge } from './ActivityBadge';

interface ActivityDetailsModalProps {
  activity: AuditActivity | null;
  onClose: () => void;
}

export const ActivityDetailsModal: React.FC<ActivityDetailsModalProps> = ({
  activity,
  onClose
}) => {
  if (!activity) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Activity Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Activity Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
            <ActivityBadge actionType={activity.actionType} />
          </div>

          {/* Timestamp */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timestamp</label>
            <p className="text-sm text-gray-900">{formatDate(activity.actionTimestamp)}</p>
          </div>

          {/* User Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Name</label>
              <p className="text-sm text-gray-900">{activity.userName || 'System'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Email</label>
              <p className="text-sm text-gray-900">{activity.userEmail || 'N/A'}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Role</label>
            <p className="text-sm text-gray-900">{activity.userRole || 'N/A'}</p>
          </div>

          {/* Quote Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote Reference</label>
              <p className="text-sm font-medium text-blue-600">{activity.quoteReference || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
              <p className="text-sm text-gray-900">{activity.customerName || 'N/A'}</p>
            </div>
          </div>

          {/* Technical Details */}
          {activity.ipAddress && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
              <p className="text-sm text-gray-900">{activity.ipAddress}</p>
            </div>
          )}

          {activity.userAgent && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Agent</label>
              <p className="text-sm text-gray-900 break-all">{activity.userAgent}</p>
            </div>
          )}

          {/* Changes Made */}
          {activity.changesMade && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Changes Details</label>
              <pre className="mt-1 text-xs text-gray-900 bg-gray-50 p-3 rounded overflow-x-auto">
                {JSON.stringify(activity.changesMade, null, 2)}
              </pre>
            </div>
          )}

          {/* Notes */}
          {activity.notes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <p className="text-sm text-gray-900">{activity.notes}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};